import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useSignalR } from "@/composables/signalr";
import {
  type MonthlyResultDto,
  type MonthlyPrizeSelectionDto,
  type SetMonthlySelectionsDto,
  type ChooseWinnerPrizeDto,
  type SetFinalPrizeDto,
  type PrizeSuggestionDto,
  getApiV1MonthlyResults,
  getApiV1MonthlyPrizeSelections,
  postApiV1MonthlyPrizeSelections,
  putApiV1MonthlyResultsByIdChoosePrize,
  putApiV1MonthlyResultsByIdPatchSuggestions,
  putApiV1MonthlyResultsByIdSetFinalPrize,
} from "@/client";
import type {
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
} from "@/types/signalr-events";
import { useAuthStore } from "./auth";

export const usePrizesStore = defineStore("prizes", () => {
  const prizesDataLoading = ref(false);
  const prizesDataLoaded = ref(false);
  const monthlyResults = ref<MonthlyResultDto[]>([]);
  const currentMonthSelections = ref<MonthlyPrizeSelectionDto[]>([]);
  const { subscribe, onReconnected } = useSignalR();
  // Live events sent while offline are lost; reload what this store already showed.
  onReconnected(() => {
    if (prizesDataLoaded.value) {
      fetchPrizesData().catch((err) => console.error("Resync after reconnect failed", err));
    }
  });

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  // Computed properties
  const currentMonthResult = computed(() =>
    monthlyResults.value.find(
      (result) => result.month === currentMonth && result.year === currentYear,
    ),
  );

  const monthlyWinsCount = computed(() => {
    const wins: Record<string, number> = {};
    monthlyResults.value.forEach((result) => {
      if (result.winnerUsername) {
        wins[result.winnerUsername] = (wins[result.winnerUsername] || 0) + 1;
      }
    });
    return wins;
  });

  async function fetchPrizesData() {
    prizesDataLoading.value = true;
    try {
      // Fetch monthly results
      const resultsResponse = await getApiV1MonthlyResults();
      if (resultsResponse.error) {
        console.error("Error fetching monthly results:", resultsResponse.error);
        throw new Error("Failed to fetch monthly results");
      }
      monthlyResults.value = resultsResponse.data || [];

      // Fetch current month selections
      const selectionsResponse = await getApiV1MonthlyPrizeSelections({
        query: { year: currentYear, month: currentMonth },
      });
      if (selectionsResponse.error) {
        console.error(
          "Error fetching monthly prize selections:",
          selectionsResponse.error,
        );
        throw new Error("Failed to fetch monthly prize selections");
      }
      const selections = selectionsResponse.data || [];
      currentMonthSelections.value =
        selections?.filter((s) => s.userId === useAuthStore().user?.userId) ||
        [];

      prizesDataLoaded.value = true;
    } finally {
      prizesDataLoading.value = false;
    }
  }

  async function ensurePrizesDataLoaded() {
    if (!prizesDataLoaded.value) {
      await fetchPrizesData();
    }
  }

  async function setMonthlySelections(selections: SetMonthlySelectionsDto) {
    const postResponse = await postApiV1MonthlyPrizeSelections({
      body: selections,
    });
    if (postResponse.error) {
      console.error("Error setting monthly selections:", postResponse.error);
      throw new Error("Failed to set monthly selections");
    }
    // Refresh current month selections
    const updatedResponse = await getApiV1MonthlyPrizeSelections({
      query: { year: currentYear, month: currentMonth },
    });
    if (updatedResponse.error) {
      console.error(
        "Error fetching updated monthly prize selections:",
        updatedResponse.error,
      );
      throw new Error("Failed to fetch updated monthly prize selections");
    }
    const updatedSelections = updatedResponse.data || [];
    currentMonthSelections.value =
      updatedSelections?.filter(
        (s) => s.userId === useAuthStore().user?.userId,
      ) || [];
  }

  async function chooseWinnerPrize(
    monthlyResultId: string,
    dto: ChooseWinnerPrizeDto,
  ) {
    const response = await putApiV1MonthlyResultsByIdChoosePrize({
      path: { id: monthlyResultId },
      body: dto,
    });
    if (response.error) {
      console.error("Error choosing winner prize:", response.error);
      throw new Error("Failed to choose winner prize");
    }
    // Refresh monthly results
    await fetchPrizesData();
  }

  async function patchMonthlyResultSuggestions(
    monthlyResultId: string,
    dto: SetMonthlySelectionsDto,
  ) {
    const response = await putApiV1MonthlyResultsByIdPatchSuggestions({
      path: { id: monthlyResultId },
      body: dto,
    });
    if (response.error) {
      console.error(
        "Error patching monthly result suggestions:",
        response.error,
      );
      throw new Error("Failed to patch monthly result suggestions");
    }
  }

  async function setFinalPrize(monthlyResultId: string, dto: SetFinalPrizeDto) {
    var response = await putApiV1MonthlyResultsByIdSetFinalPrize({
      path: { id: monthlyResultId },
      body: dto,
    });
    if (response.error) {
      console.error("Error setting final prize:", response.error);
      throw new Error("Failed to set final prize");
    }
    // Refresh monthly results
    await fetchPrizesData();
  }

  //#region SignalR
  subscribe<EntityUpdatedEvent<MonthlyResultDto>>(
    "MonthlyResultUpdated",
    async (event) => {
      console.debug("MonthlyResultUpdated event received:", event);
      createOrUpdateMonthlyResult(event.entity); //TODO is not mapped successfully on Server-Side
    },
  );

  subscribe<EntityCreatedEvent<MonthlyResultDto>>(
    "MonthlyResultCreated",
    async (event) => {
      console.debug("MonthlyResultCreated event received:", event);
      createOrUpdateMonthlyResult(event.entity);
    },
  );

  subscribe<EntityUpdatedEvent<PrizeSuggestionDto>>(
    "PrizeSuggestionUpdated",
    async (event) => {
      console.debug("PrizeSuggestionUpdated event received:", event);
      // Check current month selections
      const currentIndex = currentMonthSelections.value.findIndex(
        (selection) => selection.prizeSuggestionId === event.entity.id,
      );
      if (currentIndex !== -1) {
        currentMonthSelections.value[currentIndex] = event.entity;
      }
    },
  );
  subscribe<EntityDeletedEvent>("PrizeSuggestionDeleted", async (event) => {
    console.debug("PrizeSuggestionDeleted event received:", event);
    // Check current month selections
    const currentIndex = currentMonthSelections.value.findIndex(
      (selection) => selection.prizeSuggestionId === event.entityId,
    );
    if (currentIndex !== -1) {
      currentMonthSelections.value.splice(currentIndex, 1);
    }
  });

  subscribe<EntityUpdatedEvent<MonthlyPrizeSelectionDto>>(
    "MonthlyPrizeSelectionUpdated",
    async (event) => {
      console.debug("MonthlyPrizeSelectionUpdated event received:", event);
      createOrUpdateMonthlyPrizeSelection(event.entity);
    },
  );

  subscribe<EntityCreatedEvent<MonthlyPrizeSelectionDto>>(
    "MonthlyPrizeSelectionCreated",
    async (event) => {
      console.debug("MonthlyPrizeSelectionCreated event received:", event);
      createOrUpdateMonthlyPrizeSelection(event.entity);
    },
  );

  subscribe<EntityDeletedEvent>(
    "MonthlyPrizeSelectionDeleted",
    async (event) => {
      console.debug("MonthlyPrizeSelectionDeleted event received:", event);
      // Also remove from current month selections if it's from this month
      const currentIndex = currentMonthSelections.value.findIndex(
        (selection) => selection.id === event.entityId,
      );
      if (currentIndex !== -1) {
        currentMonthSelections.value.splice(currentIndex, 1);
      }
    },
  );

  function createOrUpdateMonthlyResult(result: MonthlyResultDto) {
    const index = monthlyResults.value.findIndex((r) => r.id === result.id);
    if (index === -1) {
      monthlyResults.value.push(result);
    } else {
      monthlyResults.value[index] = result;
    }
  }

  function createOrUpdateMonthlyPrizeSelection(
    selection: MonthlyPrizeSelectionDto,
  ) {
    // First, check if the selection is for the current user, month and year
    const authStore = useAuthStore();
    if (
      selection.userId !== authStore.user?.userId ||
      selection.month !== currentMonth ||
      selection.year !== currentYear
    ) {
      return; // Not relevant for current user's current month selections
    }

    // Update current month selections if it's from this month
    // Note: The selection object should have month/year info for proper filtering
    const currentIndex = currentMonthSelections.value.findIndex(
      (s) => s.id === selection.id,
    );
    if (currentIndex === -1) {
      currentMonthSelections.value.push(selection);
    } else {
      currentMonthSelections.value[currentIndex] = selection;
    }
    console.log("Current Selections after:", currentMonthSelections.value);
  }
  //#endregion

  return {
    // State
    monthlyResults,
    currentMonthSelections,
    prizesDataLoading,
    prizesDataLoaded,

    // Computed
    currentMonthResult,
    monthlyWinsCount,

    // Actions
    fetchPrizesData,
    ensurePrizesDataLoaded,
    setMonthlySelections,
    patchMonthlyResultSuggestions,
    chooseWinnerPrize,
    setFinalPrize,
  };
});
